import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Checkbox, Input, TextArea } from '@/components/ui/Input';
import { toast } from '@/components/ui/Toast';
import { routesApi } from '@/services/endpoints/masterData';
import { queryKeys } from '@/services/queryKeys';
import { ApiError } from '@/services/apiClient';
import type { Route } from '@/domain/types';

export function RouteFormModal({
  isOpen,
  onClose,
  route,
}: {
  isOpen: boolean;
  onClose: () => void;
  route?: Route | null;
}): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setActive] = useState(true);

  useEffect(() => {
    if (!isOpen) return;
    setName(route?.name ?? '');
    setDescription(route?.description ?? '');
    setActive(route?.isActive ?? true);
  }, [isOpen, route]);

  const mutation = useMutation({
    mutationFn: () =>
      route
        ? routesApi.update(route.id, { name, description: description || null, isActive })
        : routesApi.create({ name, description: description || null }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.routes.all });
      toast.success(route ? t('route.updated') : t('route.created'));
      onClose();
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : t('errors.unknown')),
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={route ? t('route.edit') : t('route.new')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => mutation.mutate()} isLoading={mutation.isPending} disabled={!name.trim()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label={t('route.name')}
          placeholder={t('route.namePlaceholder')}
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
          autoFocus
        />

        <TextArea
          label={t('route.description')}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />

        {route && (
          <Checkbox
            label={t('common.active')}
            checked={isActive}
            onChange={(event) => setActive(event.target.checked)}
          />
        )}
      </div>
    </Modal>
  );
}
